import React, { useCallback, useEffect, useRef, useState } from 'react';
import { basedAxios, useStore } from '../../store/Store';
import cx from 'classnames';
import EventLabel from '../../components/EventLabel/EventLabel';

interface IChatProps {
  myOrder: any;
  order: any;
}

const placeHolderTexts = {
  swe: {
    WriteMessage: 'Skriv ett meddelande...',
  },
  eng: {
    WriteMessage: 'Write a message...',
  },
};

export default function Chat({ myOrder, order }: IChatProps) {
  const [messages, setMessages] = useState<any>([]);
  const [messageText, setMessageText] = useState('');
  const [blockScroll, setBlockScroll] = useState<boolean>(false);

  const outerRef = useRef(null);
  const innerRef = useRef(null);

  const endRef = useRef(null);

  const { environment } = useStore();
  const { lang } = environment;

  const setMessagesSeen = useCallback(async () => {
    const unreadMessages = messages?.filter(({ receiver, seen }) => receiver === myOrder.badgeNo && seen === false);
    if (unreadMessages?.length === 0) return;

    try {
      await basedAxios.put(
        './chat/setSeen',
        unreadMessages.map(({ interactionChatId }) => interactionChatId)
      );
      const newMessages = messages.map(message => {
        if (message.receiver === myOrder.badgeNo && message.seen === false) {
          return { ...message, seen: true };
        }
        return message;
      });
      setMessages(newMessages);
    } catch (error) {
      console.error(error);
    }
  }, [myOrder, order, messages]);

  const scrollToBottom = useCallback(() => {
    if (outerRef.current && innerRef.current) {
      const outerHeight = (outerRef?.current as HTMLDivElement)?.clientHeight;
      const innerHeight = (innerRef?.current as HTMLDivElement)?.clientHeight;
      const padding = 32;

      (outerRef.current as HTMLDivElement).scrollTo({
        top: innerHeight - outerHeight + padding,
        left: 0,
      });
    }
  }, [outerRef, innerRef]);

  const getMessages = useCallback(async () => {
    try {
      const response = await basedAxios.get(`./chat/getMessages/${myOrder.badgeNo}?target=${order.badgeNo}`);
      if (response.data.length > messages.length) {
        setMessages(response.data);
      }
      if (!blockScroll) {
        scrollToBottom();
      }
    } catch (error) {
      console.error(error);
    }
  }, [myOrder, blockScroll, messages, scrollToBottom]);

  const handleSendMessage = useCallback(async () => {
    if (!messageText) return;

    const newMessage = {
      eventId: myOrder?.eventId,
      sender: myOrder?.badgeNo,
      name: myOrder?.person?.firstName + ' ' + myOrder?.person?.lastName,
      message: messageText,
      receiver: order?.badgeNo,
      inserted: new Date(),
      hidden: false,
      seen: false,
    };

    try {
      setMessages(prev => [...prev, newMessage]);
      setMessageText('');

      await basedAxios.post('./chat/sendMessage', newMessage);
    } catch (error) {}
  }, [messageText, scrollToBottom, myOrder, order]);

  const handleScroll = useCallback(
    event => {
      const { scrollTop, scrollHeight, clientHeight } = event.target;
      const atBottom = Math.abs(scrollHeight - scrollTop - clientHeight) < 10;

      if (atBottom && messages?.some(({ receiver, seen }) => receiver === myOrder.badgeNo && seen === false)) {
        setMessagesSeen();
      }
      setBlockScroll(!atBottom);
    },
    [blockScroll, setBlockScroll, messages, myOrder, setMessagesSeen]
  );

  useEffect(() => {
    getMessages();

    if ((outerRef?.current as HTMLDivElement | any)?.scrollHeight < (outerRef?.current as HTMLDivElement | any)?.clientHeight) {
      setMessagesSeen();
    }
    setMessagesSeen();

    const interval = setInterval(async () => {
      getMessages();
    }, 5000);

    return () => {
      clearInterval(interval);
    };
  }, [getMessages, setMessagesSeen, scrollToBottom]);

  return (
    <div className="relative">
      <div ref={outerRef} onScroll={handleScroll} className="h-[calc(100vh_-_295px)]  border border-divider p-4 overflow-y-auto ">
        <div ref={innerRef} className="flex gap-y-2 flex-col">
          {messages?.map(({ message, inserted, name, sender, seen }: any, index: number) => (
            <>
              <div
                className={cx(
                  'w-fit flex flex-col gap-y-2 px-2',
                  sender === myOrder.badgeNo ? 'bg-primary text-white rounded-md p-2 ml-auto pl-4' : 'bg-neutral-200 rounded-md p-2 mr-auto pr-4'
                )}
              >
                <span>{message}</span>
                <span className="text-xs text-right">{new Date(inserted).toLocaleTimeString('sv-SE').substring(0, 5)}</span>
              </div>
              {messages?.[index + 1]?.sender !== sender && (
                <span className={cx('text-sm', sender === myOrder.badgeNo ? ' ml-auto' : 'mr-auto')}>
                  {sender === myOrder.badgeNo ? `${myOrder?.person?.firstName} ${myOrder?.person?.lastName}` : name}
                  {sender === myOrder.badgeNo && (
                    <>
                      {seen ? (
                        <>
                          , <EventLabel name="Seen" groupName="CMS.MatchMaking" />
                          <i className="ri-check-double-line text-green-600 text-xs ml-1" />
                        </>
                      ) : (
                        <>
                          ,<EventLabel name="Sent" groupName="CMS.MatchMaking" />
                          <i className="ri-check-line text-gray-600 text-xs ml-1" />
                        </>
                      )}
                    </>
                  )}
                </span>
              )}
            </>
          ))}
          {messages.length === 0 && (
            <div className="bg-neutral-100 p-2 rounded-md">
              <EventLabel name="NoMessagesInfo" groupName="CMS.MatchMaking" />
            </div>
          )}
          {blockScroll && (
            <button
              className="absolute bottom-20 left-1/2 -translate-x-1/2 shadow-sm border-divider border justify-center z-30 right-2 bg-neutral-100 p-2 rounded-md flex gap-x-2 items-center"
              onClick={() => {
                setBlockScroll(false);
                scrollToBottom();
              }}
            >
              <i className="ri-arrow-down-line" />
              {messages?.some(({ receiver, seen }) => receiver === myOrder.badgeNo && seen === false) ? (
                <>
                  <EventLabel name="UnreadMessage" groupName="CMS.MatchMaking" />
                  {messages?.filter(({ receiver, seen }) => receiver === myOrder.badgeNo && seen === false)?.length}
                </>
              ) : (
                <EventLabel name="ScrollToBottom" groupName="CMS.MatchMaking" />
              )}
            </button>
          )}
          <div ref={endRef} />
        </div>
      </div>

      <div className="flex gap-x-2 p-3.5  items-center justify-center">
        <input
          onKeyDown={target => {
            if (target.key === 'Enter') {
              handleSendMessage();
            }
          }}
          maxLength={500}
          placeholder={placeHolderTexts[lang].WriteMessage}
          value={messageText}
          onChange={({ target }) => setMessageText(target.value)}
          className="bg-neutral-100 py-2 rounded-md w-full border-divider border"
        />
        <button className="bg-transparent border  border-divider rounded-md disabled:opacity-50" disabled={!messageText} onClick={handleSendMessage}>
          <i className="ri-send-plane-line  " />
        </button>
      </div>
    </div>
  );
}
